import { apiService } from '../../services/api';
// src/components/BookStructureStudioView.tsx
import React, { useState, useMemo } from 'react';
import AuthorDetailsEditor from './AuthorDetailsEditor';
import FrontMatterEditor from './FrontMatterEditor';
import BackMatterEditor from './BackMatterEditor';
import BookStructureDashboard from './BookStructureDashboard';
import SectionOrderManager from './SectionOrderManager';
import SelectionModal from './SelectionModal';
import { secondaryButtonClass, primaryButtonClass, actionButtonClass } from '../constants'; 
import {
    BookStructureElementKey,
    ProjectState,
    AuthorDetails,
    FrontMatter,
    BackMatter
} from '../types';
import { useBookStructure, useUpdateAuthorDetails, useUpdateFrontMatter, useUpdateBackMatter } from '../../hooks/useBookStructureData';
import { useQueryClient } from '@tanstack/react-query';

type BookStructureSubView = 'dashboard' | 'author' | 'frontMatter' | 'backMatter' | 'sectionOrder';

interface BookStructureStudioViewProps {
    projectState: ProjectState;
    onFrontMatterChange: <K extends keyof FrontMatter>(field: K, value: FrontMatter[K]) => void;
    onBackMatterChange: <K extends keyof BackMatter>(field: K, value: BackMatter[K]) => void;
    onAuthorDetailsChange: <K extends keyof AuthorDetails>(field: K, value: AuthorDetails[K]) => void;
    onAddBackMatterListItem: (listField: 'appendices' | 'glossary' | 'bibliography') => void;
    onDeleteBackMatterListItem: (listField: 'appendices' | 'glossary' | 'bibliography', id: string) => void;
    onUpdateBackMatterListItem: (...args: any) => void;
    onApplyTemplate?: (templateData: Partial<ProjectState>) => void;
}

const BookStructureStudioView: React.FC<BookStructureStudioViewProps> = ({
    projectState,
    onFrontMatterChange,
    onBackMatterChange,
    onAuthorDetailsChange,
    onAddBackMatterListItem,
    onDeleteBackMatterListItem,
    onUpdateBackMatterListItem,
    onApplyTemplate
}) => {
    const [activeSubView, setActiveSubView] = useState<BookStructureSubView>('dashboard');
    const [isLoading, setIsLoading] = useState(false);
    const [showSelectionModal, setShowSelectionModal] = useState(false);
    const queryClient = useQueryClient();
    
    // Use dedicated book structure mutation hooks for saving
    const updateAuthorDetailsMutation = useUpdateAuthorDetails();
    const updateFrontMatterMutation = useUpdateFrontMatter();
    const updateBackMatterMutation = useUpdateBackMatter();

    // Load book structure data from API only when project state is missing the data
    const projectId = projectState?.projectId || '';
    
    // Check if we have meaningful book structure data in project state
    const hasBookStructureInState = !!(
        (projectState?.authorDetails && Object.values(projectState.authorDetails).some(value => value && String(value).trim() !== '')) ||
        (projectState?.frontMatter && Object.values(projectState.frontMatter).some(value => value && String(value).trim() !== '')) ||
        (projectState?.backMatter && Object.values(projectState.backMatter).some(value => value && String(value).trim() !== ''))
    );
    
    // Always fetch from API to ensure we have the latest book structure data (consistent with BookPreview)
    const shouldFetchFromAPI = true;
    const { data: bookStructureData, isLoading: bookStructureLoading } = useBookStructure(projectId, !!projectId && shouldFetchFromAPI);
    
    // Debug logging for data population investigation
    console.log('📊 [BOOK-STRUCTURE-STUDIO] Data status:', {
        projectId,
        hasProjectState: !!projectState,
        hasBookStructureData: !!bookStructureData,
        projectStateAuthorDetails: projectState?.authorDetails ? Object.keys(projectState.authorDetails).length : 0,
        bookStructureAuthorDetails: bookStructureData?.authorDetails ? Object.keys(bookStructureData.authorDetails).length : 0,
        shouldFetchFromAPI,
        bookStructureLoading,
        // Enhanced debugging for merge issue
        projectStateAuthorBio: projectState?.authorDetails?.authorBio || 'MISSING',
        bookStructureAuthorBio: bookStructureData?.authorDetails?.authorBio || 'MISSING',
        projectStateHasEmptyAuthorDetails: projectState?.authorDetails && Object.values(projectState.authorDetails).every(v => !v || String(v).trim() === ''),
        bookStructureHasAuthorBio: !!(bookStructureData?.authorDetails?.authorBio && String(bookStructureData.authorDetails.authorBio).trim() !== '')
    });

    // Merge project state with book structure data to get the most up-to-date information
    const mergedProjectState = useMemo(() => {
        if (!projectState) return projectState;

        // Smart merge: Use API data when local state is empty/missing, but preserve actual local edits
        const mergeSection = (localSection: any, apiSection: any) => {
            // If local section doesn't exist or is empty object with no meaningful data
            if (!localSection || 
                (typeof localSection === 'object' && 
                 Object.keys(localSection).length === 0) ||
                (typeof localSection === 'object' && 
                 Object.values(localSection).every(value => !value || String(value).trim() === ''))
            ) {
                return apiSection || localSection;
            }
            
            // If we have local data with content, keep it but merge with API data for missing fields
            if (apiSection && typeof localSection === 'object' && typeof apiSection === 'object') {
                const merged = { ...apiSection };
                Object.entries(localSection).forEach(([key, value]) => {
                    if (value && String(value).trim() !== '') {
                        merged[key] = value;
                    }
                });
                return merged;
            }
            
            return localSection;
        };

        const merged = {
            ...projectState,
            authorDetails: mergeSection(projectState.authorDetails, bookStructureData?.authorDetails),
            frontMatter: mergeSection(projectState.frontMatter, bookStructureData?.frontMatter),
            backMatter: mergeSection(projectState.backMatter, bookStructureData?.backMatter),
        };

        // Debug the merge result
        console.log('🔄 [BOOK-STRUCTURE-STUDIO] Merge result:', {
            mergedAuthorDetailsKeys: merged.authorDetails ? Object.keys(merged.authorDetails).length : 0,
            mergedAuthorBio: merged.authorDetails?.authorBio || 'MISSING',
            mergeWasNeeded: (projectState?.authorDetails && Object.values(projectState.authorDetails).every(v => !v || String(v).trim() === '')),
            apiDataUsed: !!(bookStructureData?.authorDetails && merged.authorDetails === bookStructureData.authorDetails)
        });

        return merged;
    }, [projectState, bookStructureData]);

    const renderSubView = () => {
        switch (activeSubView) {
            case 'dashboard':
                return (
                    <BookStructureDashboard
                        projectState={mergedProjectState}
                        onNavigateToSection={setActiveSubView}
                        onGenerateElements={handleOpenSelectionModal}
                        onGenerateSelectedElements={handleSelectiveGeneration}
                        isGenerating={isLoading || bookStructureLoading}
                        onApplyTemplate={onApplyTemplate}
                    />
                );
            case 'author':
                // Removed excessive console logging to prevent input lag
                
                return (
                    <AuthorDetailsEditor
                        authorDetails={mergedProjectState.authorDetails || {}}
                        onAuthorDetailsChange={onAuthorDetailsChange}
                        projectId={projectId}
                    />
                );
            case 'frontMatter':
                return (
                    <FrontMatterEditor
                        frontMatter={mergedProjectState.frontMatter || {}}
                        onFrontMatterChange={onFrontMatterChange}
                    />
                );
            case 'backMatter':
                return (
                    <BackMatterEditor
                        backMatter={mergedProjectState.backMatter || {}}
                        onBackMatterChange={onBackMatterChange}
                        onAddListItem={onAddBackMatterListItem}
                        onDeleteListItem={onDeleteBackMatterListItem}
                        onUpdateListItem={onUpdateBackMatterListItem}
                    />
                );
            case 'sectionOrder':
                return (
                    <SectionOrderManager
                        projectState={mergedProjectState}
                        onFrontMatterChange={onFrontMatterChange}
                        onBackMatterChange={onBackMatterChange}
                    />
                );
            default:
                return <p>Select a section to edit.</p>;
        }
    };

    // Dedicated book structure save function
    const handleSaveBookStructure = async () => {
        if (!projectId || !mergedProjectState) {
            console.error('❌ Cannot save: Missing project ID or state');
            return;
        }
        
        try {
            setIsLoading(true);
            
            // Save author details if they exist
            if (mergedProjectState.authorDetails && 
                Object.values(mergedProjectState.authorDetails).some(value => value && String(value).trim() !== '')) {
                
                console.log('💾 Saving author details to dedicated endpoint:', {
                    projectId,
                    authorDetails: mergedProjectState.authorDetails
                });
                
                await updateAuthorDetailsMutation.mutateAsync({
                    projectId,
                    authorDetails: mergedProjectState.authorDetails
                });
            }
            
        } catch (error: any) {
            console.error('❌ Book structure save failed:', error);
            alert(`❌ Save failed: ${error.message}\n\nPlease try again.`);
        } finally {
            setIsLoading(false);
        }
    };

    const navButtonClass = (view: BookStructureSubView) => 
        activeSubView === view 
            ? `${primaryButtonClass} opacity-100` 
            : `${secondaryButtonClass} hover:bg-gray-600 opacity-80`;

    // Selective generation handler for the new interface
    const handleSelectiveGeneration = async (selectedElements: BookStructureElementKey[]) => {

        if (!mergedProjectState || !projectId) {
            console.error('❌ Project ID is missing from mergedProjectState:', mergedProjectState);
            alert('❌ Error: Project ID is missing. Please refresh the page and try again.');
            return;
        }

        if (selectedElements.length === 0) {
            alert('ℹ️ Please select at least one element to generate.');
            return;
        }

        setIsLoading(true);

        try {
            const requestPayload = {
                projectId: projectId,
                elementKeys: selectedElements,
                context: {
                    authorDetails: mergedProjectState.authorDetails,
                    bookMetadata: mergedProjectState.bookMetadata,
                    worldBuildingData: mergedProjectState.blueprint?.worldBuildingSuggestions,
                    outlineIdea: mergedProjectState.outlineIdea,
                    // Image generation context
                    titleImagePrompt: mergedProjectState.frontMatter?.titleImagePrompt || '',
                    titleImageAspectRatio: mergedProjectState.frontMatter?.titleImageAspectRatio || '152:229', // Trade Paperback 6x9
                    backCoverImagePrompt: mergedProjectState.backMatter?.backCoverImagePrompt || '',
                    backCoverImageAspectRatio: mergedProjectState.backMatter?.backCoverImageAspectRatio || '152:229', // Trade Paperback 6x9
                }
            };

            console.log('🔍 Selective generation payload:', {
                projectId: requestPayload.projectId,
                elementKeysCount: requestPayload.elementKeys.length,
                selectedElements,
                hasContext: !!requestPayload.context,
            });

            const result = await apiService.post('/api/book-structure/generate-batch', requestPayload);

            if (result.success && result.data) {
                // Apply generated content to the project state
                const generatedData = result.data;

                // Debug: Check what was actually generated

                // Debug: Check if image prompts are in the response
                const hasTitle = 'titleImagePrompt' in generatedData;
                const hasBack = 'backCoverImagePrompt' in generatedData;
                const titleValue = generatedData.titleImagePrompt;
                const backValue = generatedData.backCoverImagePrompt;
                console.log('🖼️ Image prompts in response:', {
                    hasTitle,
                    hasBack,
                    titleLength: titleValue?.length || 0,
                    backLength: backValue?.length || 0,
                    titlePreview: titleValue?.substring(0, 100) || 'undefined',
                    backPreview: backValue?.substring(0, 100) || 'undefined'
                });

                // Update front matter fields
                const frontMatterFields: (keyof FrontMatter)[] = [
                    'titlePageContent', 'copyrightPageContent', 'dedication', 'epigraph',
                    'tableOfContents', 'foreword', 'forewordBy', 'preface', 'acknowledgmentsFront',
                    'prologue', 'introduction', 'titleImagePrompt'
                ];

                const frontMatterUpdates: Partial<FrontMatter> = {};
                frontMatterFields.forEach(field => {
                    // Check for the field in the generated data using direct property access
                    if (generatedData[field] && typeof generatedData[field] === 'string') {
                        const value = generatedData[field];
                        frontMatterUpdates[field] = value;
                        onFrontMatterChange(field, value);
                    }
                });

                // Update back matter fields
                const backMatterFields: (keyof BackMatter)[] = [
                    'epilogue', 'afterword', 'authorsNote', 'acknowledgmentsBack',
                    'aboutTheAuthor', 'readingGroupGuide', 'teaserNextBook', 'index', 'backCoverImagePrompt'
                ];

                const backMatterUpdates: Partial<BackMatter> = {};
                backMatterFields.forEach(field => {
                    // Check for the field in the generated data using direct property access
                    if (generatedData[field] && typeof generatedData[field] === 'string') {
                        const value = generatedData[field];
                        (backMatterUpdates as any)[field] = value;
                        onBackMatterChange(field, value as any);
                    }
                });

                // Update author details fields
                const authorDetailsFields: (keyof AuthorDetails)[] = [
                    'isbn', 'publisher', 'edition', 'copyrightNotice'
                ];

                const authorDetailsUpdates: Partial<AuthorDetails> = {};
                authorDetailsFields.forEach(field => {
                    if (generatedData[field as BookStructureElementKey]) {
                        const value = generatedData[field as BookStructureElementKey];
                        authorDetailsUpdates[field] = value;
                        onAuthorDetailsChange(field, value);
                    }
                });

                // Save generated data to database using React Query mutation hooks
                const savePromises: Promise<any>[] = [];

                if (Object.keys(frontMatterUpdates).length > 0) {

                    const currentFrontMatter = mergedProjectState.frontMatter || {};
                    const mergedFrontMatter = { ...currentFrontMatter, ...frontMatterUpdates };

                    const frontMatterSave = updateFrontMatterMutation.mutateAsync({
                        projectId: projectId,
                        frontMatter: mergedFrontMatter
                    });
                    savePromises.push(frontMatterSave);
                }

                if (Object.keys(authorDetailsUpdates).length > 0) {

                    const currentAuthorDetails = mergedProjectState.authorDetails || {};
                    const mergedAuthorDetails = { ...currentAuthorDetails, ...authorDetailsUpdates };

                    const authorDetailsSave = updateAuthorDetailsMutation.mutateAsync({
                        projectId: projectId,
                        authorDetails: mergedAuthorDetails
                    });
                    savePromises.push(authorDetailsSave);
                }

                if (Object.keys(backMatterUpdates).length > 0) {

                    const currentBackMatter = mergedProjectState.backMatter || {};
                    const mergedBackMatter = { ...currentBackMatter, ...backMatterUpdates };

                    const backMatterSave = updateBackMatterMutation.mutateAsync({
                        projectId: projectId,
                        backMatter: mergedBackMatter
                    });
                    savePromises.push(backMatterSave);
                }

                // Wait for all saves to complete - mutation hooks handle cache invalidation automatically
                try {
                    if (savePromises.length > 0) {
                        await Promise.all(savePromises);
                    }
                } catch (saveError: any) {
                    console.error('❌ Database save failed:', saveError);
                    alert(`⚠️ Generation successful but save failed: ${saveError.message}\n\nData is in your session but may not persist. Please manually save.`);
                }

                // Show success message
                const successCount = Object.keys(generatedData).length;
                const totalCount = selectedElements.length;

                alert(`✅ Successfully generated ${successCount} of ${totalCount} selected elements!\n\n${result.message}`);

                if (result.failedElements && result.failedElements.length > 0) {
                    console.warn('⚠️ Some elements failed to generate:', result.failedElements);
                }
            } else {
                throw new Error(result.message || 'Generation failed');
            }
        } catch (error: any) {
            console.error('❌ Selective generation failed:', error);
            alert(`❌ Selective generation failed: ${error.message}\n\nPlease try again or generate elements individually.`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleConsolidatedGeneration = async () => {
        // Define which elements to attempt to generate in one go - ALL AI-generatable elements
        const elementsToRequest: string[] = [
            // Front Matter elements
            'titlePageContent', 'copyrightPageContent', 'dedication', 'epigraph',
            'tableOfContents', 'foreword', 'forewordBy', 'preface', 'acknowledgmentsFront',
            'prologue', 'introduction',
            // Back Matter elements
            'epilogue', 'afterword', 'authorsNote', 'acknowledgmentsBack',
            'aboutTheAuthor', 'readingGroupGuide', 'teaserNextBook', 'index',
            // Author Details / Publishing elements
            'isbn', 'publisher', 'edition', 'copyrightNotice'
        ];
        
        // Filter based on fiction/non-fiction if needed (e.g. only prologue OR introduction)
        const filteredElements = elementsToRequest.filter(el => {
            if (mergedProjectState.bookMetadata.fictionVsNonFiction === 'Fiction') {
                return el !== 'introduction';
            } else if (mergedProjectState.bookMetadata.fictionVsNonFiction === 'Non-fiction') {
                return el !== 'prologue';
            }
            return true; // Include both if not specified, AI prompt can handle it
        });

        console.log('🔍 Project state debug:', {
            hasProjectState: !!mergedProjectState,
            projectStateId: mergedProjectState?.projectId,
            projectStateProjectId: mergedProjectState?.projectId,
            extractedProjectId: projectId,
            projectStateKeys: Object.keys(mergedProjectState || {}),
        });
        
        if (!mergedProjectState || !projectId) {
            console.error('❌ Project ID is missing from mergedProjectState:', mergedProjectState);
            alert('❌ Error: Project ID is missing. Please refresh the page and try again.');
            return;
        }
        
        setIsLoading(true);

        try {
            const requestPayload = {
                projectId: projectId,
                elementKeys: filteredElements,
                context: {
                    authorDetails: mergedProjectState.authorDetails,
                    bookMetadata: mergedProjectState.bookMetadata,
                    worldBuildingData: mergedProjectState.blueprint?.worldBuildingSuggestions,
                    outlineIdea: mergedProjectState.outlineIdea,
                    // Image generation context
                    titleImagePrompt: mergedProjectState.frontMatter?.titleImagePrompt || '',
                    titleImageAspectRatio: mergedProjectState.frontMatter?.titleImageAspectRatio || '152:229', // Trade Paperback 6x9
                    backCoverImagePrompt: mergedProjectState.backMatter?.backCoverImagePrompt || '',
                    backCoverImageAspectRatio: mergedProjectState.backMatter?.backCoverImageAspectRatio || '152:229', // Trade Paperback 6x9
                }
            };
            
            console.log('🔍 Request payload debug:', {
                projectId: requestPayload.projectId,
                elementKeysCount: requestPayload.elementKeys.length,
                hasContext: !!requestPayload.context,
                authorName: requestPayload.context.authorDetails?.authorName || 'Not set',
                payloadSize: JSON.stringify(requestPayload).length
            });
            
            const result = await apiService.post('/api/book-structure/generate-batch', requestPayload);

            if (result.success && result.data) {
                // Apply generated content to the project state
                const generatedData = result.data;
                
                // Update front matter fields - ALL front matter elements that can be AI generated
                const frontMatterFields: (keyof FrontMatter)[] = [
                    'titlePageContent', 'copyrightPageContent', 'dedication', 'epigraph',
                    'tableOfContents', 'foreword', 'forewordBy', 'preface', 'acknowledgmentsFront',
                    'prologue', 'introduction', 'titleImagePrompt'
                ];

                const frontMatterUpdates: Partial<FrontMatter> = {};
                frontMatterFields.forEach(field => {
                    // Check for the field in the generated data using direct property access
                    if (generatedData[field] && typeof generatedData[field] === 'string') {
                        const value = generatedData[field];
                        frontMatterUpdates[field] = value;
                        onFrontMatterChange(field, value);
                    }
                });

                // Update back matter fields - ALL back matter elements that can be AI generated
                const backMatterFields: (keyof BackMatter)[] = [
                    'epilogue', 'afterword', 'authorsNote', 'acknowledgmentsBack',
                    'aboutTheAuthor', 'readingGroupGuide', 'teaserNextBook', 'index', 'backCoverImagePrompt'
                ];

                const backMatterUpdates: Partial<BackMatter> = {};
                backMatterFields.forEach(field => {
                    // Check for the field in the generated data using direct property access
                    if (generatedData[field] && typeof generatedData[field] === 'string') {
                        const value = generatedData[field];
                        (backMatterUpdates as any)[field] = value;
                        onBackMatterChange(field, value as any);
                    }
                });

                // Update author details fields - AI-generated publishing information  
                const authorDetailsFields: (keyof AuthorDetails)[] = [
                    'isbn', 'publisher', 'edition', 'copyrightNotice'
                ];
                
                const authorDetailsUpdates: Partial<AuthorDetails> = {};
                authorDetailsFields.forEach(field => {
                    if (generatedData[field as BookStructureElementKey]) {
                        const value = generatedData[field as BookStructureElementKey];
                        authorDetailsUpdates[field] = value;
                        onAuthorDetailsChange(field, value);
                    }
                });

                // 🔥 CRITICAL: Save generated data to database using dedicated API endpoints
                const savePromises: Promise<any>[] = [];
                
                if (Object.keys(frontMatterUpdates).length > 0) {
                    
                    // Get existing front matter data and merge with updates
                    const currentFrontMatter = mergedProjectState.frontMatter || {};
                    const mergedFrontMatter = { ...currentFrontMatter, ...frontMatterUpdates };
                    
                    const frontMatterSave = apiService.put(`/api/book-structure/${projectId}/front-matter`, {
                        frontMatter: mergedFrontMatter 
                    });
                    savePromises.push(frontMatterSave);
                }
                
                if (Object.keys(authorDetailsUpdates).length > 0) {
                    
                    // Get existing author details and merge with updates
                    const currentAuthorDetails = mergedProjectState.authorDetails || {};
                    const mergedAuthorDetails = { ...currentAuthorDetails, ...authorDetailsUpdates };
                    
                    const authorDetailsSave = apiService.put(`/api/book-structure/${projectId}/author-details`, {
                        authorDetails: mergedAuthorDetails 
                    });
                    savePromises.push(authorDetailsSave);
                }
                
                if (Object.keys(backMatterUpdates).length > 0) {
                    
                    // Get existing back matter data and merge with updates
                    const currentBackMatter = mergedProjectState.backMatter || {};
                    const mergedBackMatter = { ...currentBackMatter, ...backMatterUpdates };
                    
                    const backMatterSave = apiService.put(`/api/book-structure/${projectId}/back-matter`, {
                        backMatter: mergedBackMatter 
                    });
                    savePromises.push(backMatterSave);
                }

                // Wait for all saves to complete before showing success message
                try {
                    if (savePromises.length > 0) {
                        await Promise.all(savePromises);
                        
                        // 🔄 Invalidate React Query cache to ensure fresh data loads when navigating
                        await queryClient.invalidateQueries({ queryKey: ['book-structure', projectId] });
                        // CRITICAL FIX: Invalidate project summary cache so BookPreview gets fresh book structure data
                        await queryClient.invalidateQueries({ queryKey: ['story-project-summary', projectId] });
                    }
                } catch (saveError: any) {
                    console.error('❌ Database save failed:', saveError);
                    alert(`⚠️ Generation successful but save failed: ${saveError.message}\n\nData is in your session but may not persist. Please manually save.`);
                }

                // Show success message
                const successCount = Object.keys(generatedData).length;
                const totalCount = filteredElements.length;
                
                alert(`✅ Successfully generated ${successCount} of ${totalCount} book elements!\n\n${result.message}`);
                
                if (result.failedElements && result.failedElements.length > 0) {
                    console.warn('⚠️ Some elements failed to generate:', result.failedElements);
                }
            } else {
                throw new Error(result.message || 'Generation failed');
            }
        } catch (error: any) {
            console.error('❌ Batch generation failed:', error);
            alert(`❌ Batch generation failed: ${error.message}\n\nPlease try again or generate elements individually.`);
        } finally {
            setIsLoading(false);
        }
    };

    // New handler to open selection modal instead of immediate generation
    const handleOpenSelectionModal = () => {
        setShowSelectionModal(true);
    };

    // Handler for when user proceeds from selection modal
    const handleProceedWithSelectedGeneration = (selectedElements: BookStructureElementKey[]) => {
        setShowSelectionModal(false);
        handleSelectiveGeneration(selectedElements);
    };

    return (
        <div className="flex flex-col h-full bg-white rounded-lg shadow-xl overflow-hidden">
            <header className="p-4 border-b bg-gray-50">
                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-semibold text-gray-800">Book Structure Studio</h2>
                    <div className="flex space-x-2">
                        <button
                            onClick={handleSaveBookStructure}
                            disabled={isLoading}
                            className={`${primaryButtonClass}`}
                            title="Save all book structure data to the database"
                        >
                            {isLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                            onClick={handleOpenSelectionModal}
                            disabled={isLoading}
                            className={`${actionButtonClass}`}
                            title="Generate multiple text elements like dedication, prologue, epilogue, etc., with AI based on your project's context."
                        >
                            {isLoading ? 'Generating Elements...' : 'Generate Multiple Text Elements (AI)'}
                        </button>
                    </div>
                </div>
                <nav className="mt-3 flex space-x-2">
                    <button onClick={() => setActiveSubView('dashboard')} className={navButtonClass('dashboard')}>
                        📊 Overview
                    </button>
                    <button onClick={() => setActiveSubView('author')} className={navButtonClass('author')}>
                        👤 Author & Publishing Info
                    </button>
                    <button onClick={() => setActiveSubView('frontMatter')} className={navButtonClass('frontMatter')}>
                        📖 Front Matter
                    </button>
                    <button onClick={() => setActiveSubView('backMatter')} className={navButtonClass('backMatter')}>
                        📚 Back Matter
                    </button>
                    <button onClick={() => setActiveSubView('sectionOrder')} className={navButtonClass('sectionOrder')}>
                        🔄 Section Order
                    </button>
                </nav>
            </header>
            <div className="flex-grow p-1 md:p-2 overflow-y-auto bg-gray-100">
                {renderSubView()}
            </div>
            <footer className="p-3 text-xs text-gray-500 border-t bg-gray-50 text-center">
                 Changes made here are part of your current project. Remember to "Save Project" to persist them.
            </footer>

            {/* Selection Modal */}
            <SelectionModal
                isOpen={showSelectionModal}
                onClose={() => setShowSelectionModal(false)}
                onProceed={handleProceedWithSelectedGeneration}
                projectState={mergedProjectState}
                isGenerating={isLoading}
            />
        </div>
    );
};

export default BookStructureStudioView;